import {
  Button,
  Card,
  Container,
  Grid,
  Group,
  Image,
  Indicator,
  Modal,
  Popover,
  Progress,
  rem,
  RingProgress,
  LoadingOverlay,
  Stack,
  Text, Tooltip
} from '@mantine/core';
import {
  IconDownload,
  IconPaperclip,
  IconPhoto,
  IconThumbDown,
  IconTrash,
  IconUpload,
  IconX
} from '@tabler/icons-react';
import { Dropzone, DropzoneProps } from '@mantine/dropzone';
import {
  generateDataURI,
  getAttachmentFileSizeString,
  getFileBase64,
  getModuleQuery,
  truncateString,
  useAgLazyQuery,
  useAgMutation,
  errorBoundaryWrapper
} from '@repo/utils';
import { useParams } from 'react-router';
import React, {useCallback, useState, useRef, useEffect} from 'react';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import { AxiosProgressEvent } from 'axios';
import { useDisclosure, useSetState } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  AimApi,
  ModuleIdentifier,
  useDeleteAttachmentMutation
} from "@repo/services";
import {useAimContext} from "@repo/services";
import classes from './Attachments.module.css';

interface AttachmentProps {
  idType: ModuleIdentifier;
  isNew?: boolean;
}

function Attachments({ idType, isNew = false }: AttachmentProps) {
  const fileNameMaxLength = 35;
  const {
    entityNotes,
    setEntityNotes,
    entityAttachments,
    setEntityAttachments,
    clearEntityAttachments,
    clearEntityNotes
  } = useAimContext();

  const openRef = useRef<any>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const { id } = useParams();
  const attachmentQuery = getModuleQuery(idType);
  const [
    attachments = [],
    attachmentsIsLoading,
    attachmentsIsSuccess,
    attachmentsIsFetching,
    attachmentsIsError,
    attachmentsError,
    attachmentsTrigger
  ] = useAgLazyQuery<any[]>(id, attachmentQuery.attachments);
  const { trigger: triggerUpload, isLoading: uploadIsLoading } = useAgMutation(
      attachmentQuery.addAttachments,
      () => {
        setUploadProgress(0);
        attachmentsTrigger(id);
        notifications.show({
          title: 'Upload Successful',
          message: 'The attachment was successfully uploaded'
        });
      },
      () => {
        setUploadProgress(0);
      }
  );
  const { trigger: triggerDelete } = useAgMutation(useDeleteAttachmentMutation, () => {
    notifications.show({
      title: 'Delete Successful',
      message: 'The attachment was successfully deleted'
    });
  });
  const [selectedAttachmentPid, setSelectedAttachmentPid] = useState<string | undefined>(undefined);
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(0);
  const [fileDownloadsProgress, setFileDownloadsProgress] = useSetState<{ [key: string]: number }>({});
  const [dropzoneLoading, setDropzoneLoading] = useState<boolean>(false);

  const toggleLoading = () => {
    setDropzoneLoading(true);
    setTimeout(() => {setDropzoneLoading(false)},1000)
  }

  const handleSelectAttachment = (pid: string) => {
    setSelectedAttachmentPid(pid);
  };

  const handleClosePreview = () => {
    setSelectedAttachmentPid(undefined);
  };

  const handleRemoveNewOpportunityAttachment = (attachmentIndex: number) => {
    const newArr = [...entityAttachments.attachments];
    newArr.splice(attachmentIndex, 1);
    setEntityAttachments({ entity: idType, attachments: newArr });
  };

  const processAttachments = async (fileList: any[]) => {
    const vals = fileList.map((file) =>
        getFileBase64(file).then((val) => {
          return { fileName: file.name, base64: val };
        })
    );
    return Promise.all(vals);
  };

  const uploadFiles = useCallback(
      async (fileList: any[]) => {
        if (id && id?.toString() !== '0') {
          processAttachments(fileList).then((values) => {
            const addAttachmentParams: any = {
              [ModuleIdentifier[idType]]: id,
              attachments: values,
              onUploadProgress: ({ progress = 0 }: AxiosProgressEvent) => {
                setUploadProgress(progress * 100);
              }
            };
            triggerUpload(addAttachmentParams);
          });
        }
      },
      [triggerUpload, id, idType]
  );

  const handleUpload = (fileList: any[]) => {
    if (isNew) {
      const allFiles = [...entityAttachments.attachments, ...fileList];
      setEntityAttachments({ entity: idType, attachments: allFiles });
    } else {
      uploadFiles(fileList).then();
    }
  };

  const handleDropzoneClick = () => {
    toggleLoading();
    openRef.current?.();
  }

  const downloadProgress = async (progress = 0, file: any) => {
    setFileDownloadsProgress({ [file.pid]: progress * 100 });
  };
  const downloadFile = async (file: any) => {
    if (fileDownloadsProgress[file.pid]! >= 0) return;
    try {
      await downloadProgress(0, file);
      const { data: blob } = await AimApi.aim.attachments.getFileBlob({
        pid: file.pid,
        onDownloadProgress: ({ progress }) => downloadProgress(progress || 0, file)
      });
      const uri = await generateDataURI(blob);
      const a = document.createElement('a');
      a.href = uri;
      a.download = file.filename!;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error(e);
    }
    setFileDownloadsProgress({ [file.uid]: -1 });
  };

  return (
      <>
        <Modal opened={opened} onClose={uploadIsLoading ? () => {} : close} title='Attachments' size={'70rem'}>
          <Container h={'100%'}>
            <AttachmentPreviewModal pid={selectedAttachmentPid} handleClose={handleClosePreview} />
            {!uploadIsLoading && (
                <Dropzone
                    openRef={openRef}
                    onClick={handleDropzoneClick}
                    onDrop={(files) => handleUpload(files)}
                    onReject={(files) => console.log('rejected files', files)}
                    // maxSize={5 * 1024 ** 2}
                    /*{...dropzoneProps}*/
                >
                  <LoadingOverlay visible={dropzoneLoading} zIndex={1000} />
                  <Group justify='center' gap='xl' mih={220} style={{ pointerEvents: 'none' }}>
                    <Dropzone.Accept>
                      <IconUpload
                          style={{ width: rem(52), height: rem(52), color: 'var(--mantine-color-blue-6)' }}
                          stroke={1.5}
                      />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <IconX
                          style={{ width: rem(52), height: rem(52), color: 'var(--mantine-color-red-6)' }}
                          stroke={1.5}
                      />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <IconPhoto
                          style={{ width: rem(52), height: rem(52), color: 'var(--mantine-color-dimmed)' }}
                          stroke={1.5}
                      />
                    </Dropzone.Idle>

                    <div>
                      <Text size='xl' inline>
                        Drag images here or click to select files
                      </Text>
                      <Text size='sm' c='dimmed' inline mt={7}>
                        Attach as many files as you like, each file should not exceed 5mb
                      </Text>
                    </div>
                  </Group>
                </Dropzone>
            )}
            {uploadIsLoading && (
                <Container h='250px'>
                  <Stack mt={55}>
                    <Text style={{ textAlign: 'center' }}>Upload in Progress...</Text>
                    <Progress
                        value={uploadProgress || 0}
                        //@ts-ignore
                        label={`${uploadProgress || 0}%`}
                        size='xl'
                        radius='xl'
                    />
                  </Stack>
                </Container>
            )}
            <Container p={0} style={{ height: '500px', overflowY: 'scroll' }}>
              {isNew && entityAttachments?.attachments?.map((a, i) => (
                  <Card
                      key={i}
                      mt={10}
                      shadow='sm'
                      padding='lg'
                      radius='md'
                      withBorder
                      style={{ cursor: 'pointer' }}
                  >
                    {/*<Group justify="space-between">*/}
                    <Grid>
                      <Grid.Col span={8}>
                        <Group>
                          <Image h={40} w={40} radius='md' />
                          <Text>{truncateString(a.name, fileNameMaxLength)}</Text>
                        </Group>
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Group mt={10}>
                          <Popover width={400} withArrow>
                            <Popover.Target>
                              <IconTrash color='gray' />
                            </Popover.Target>
                            <Popover.Dropdown>
                              <Group>
                                <Text>Are you sure you want to delete this attachment?</Text>
                                <Button
                                    color='red'
                                    mt={20}
                                    rightSection={<IconThumbDown size={14} />}
                                    onClick={() => handleRemoveNewOpportunityAttachment(i)}
                                >
                                  Delete
                                </Button>
                              </Group>
                            </Popover.Dropdown>
                          </Popover>
                          <Text>{a.size ? getAttachmentFileSizeString(a.size) : 0}</Text>
                        </Group>
                      </Grid.Col>
                      {/*</Group>*/}
                    </Grid>
                  </Card>
              ))}
              {!isNew && attachments?.map((a) => (
                  <Card
                      key={a.pid}
                      mt={10}
                      shadow='sm'
                      padding='lg'
                      radius='md'
                      withBorder
                      style={{ cursor: 'pointer' }}
                  >
                    {/*<Group justify="space-between">*/}
                    <Grid>
                      <Grid.Col span={8}>
                        <Group onClick={() => handleSelectAttachment(a.pid)}>
                          <Image h={40} w={40} radius='md' src={a.thumbnailUrl} />
                          <Text>{truncateString(a.filename, fileNameMaxLength)}</Text>
                        </Group>
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Group mt={10}>
                          {fileDownloadsProgress[a.pid] > 0 && fileDownloadsProgress[a.pid] < 100 && (
                              <RingProgress
                                  size={20}
                                  thickness={16}
                                  label=''
                                  sections={[
                                    {
                                      value: fileDownloadsProgress[a.pid],
                                      color: 'cyan',
                                      tooltip: 'Progress'
                                    }
                                  ]}
                              />
                          )}
                          {(!fileDownloadsProgress[a.pid] ||
                              fileDownloadsProgress[a.pid] === 0 ||
                              fileDownloadsProgress[a.pid] === 100) && (
                              <IconDownload
                                  color='gray'
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => downloadFile(a)}
                              />
                          )}
                          <Popover width={400} withArrow>
                            <Popover.Target>
                              <IconTrash color='gray' />
                            </Popover.Target>
                            <Popover.Dropdown>
                              <Group>
                                <Text>Are you sure you want to delete this attachment?</Text>
                                <Button
                                    color='red'
                                    mt={20}
                                    rightSection={<IconThumbDown size={14} />}
                                    onClick={() => triggerDelete(a.pid)}
                                >
                                  Delete
                                </Button>
                              </Group>
                            </Popover.Dropdown>
                          </Popover>
                          <Text>{a.size ? getAttachmentFileSizeString(a.size) : 0}</Text>
                        </Group>
                      </Grid.Col>
                      {/*</Group>*/}
                    </Grid>
                  </Card>
              ))}
            </Container>
          </Container>
        </Modal>

        <Tooltip label="Attachments">
          <Indicator
              inline
              classNames={classes}
              label={isNew ? entityAttachments.attachments?.length : attachments?.length}
              disabled={isNew ? entityAttachments.attachments?.length === 0 : attachments?.length === 0}
              size={16}
          >
            <Button mr={8} onClick={open} variant='default'>
              <IconPaperclip />
            </Button>
          </Indicator>
        </Tooltip>
      </>
  );
}

export default errorBoundaryWrapper(Attachments)
